import React, { useState, useEffect } from 'react';
import { ArrowLeft, Upload, Edit, X, Check } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import kycService from '../../services/kycService';
import PhoneInput from '../common/PhoneInput';

const KYCModal = ({ onClose, onSubmit }) => {
  const { t } = useLanguage();
  const { user, refreshUser } = useAuth();
  
  const [formData, setFormData] = useState({
    primaryDriverLicenseFront: null,
    primaryDriverLicenseBack: null,
    primaryReferenceNumber: user?.phoneNumber || '',
    secondaryDriverLicenseFront: null,
    secondaryDriverLicenseBack: null,
    secondaryReferenceNumber: '',
  });

  const [previews, setPreviews] = useState({
    primaryDriverLicenseFront: null,
    primaryDriverLicenseBack: null,
    secondaryDriverLicenseFront: null,
    secondaryDriverLicenseBack: null,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Bloquer le scroll de la page quand le modal est ouvert
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleImageUpload = (field, file) => {
    if (!file) return;

    // Validation du type de fichier
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, [field]: 'Veuillez sélectionner une image valide' }));
      return;
    }

    // Validation de la taille (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [field]: 'L\'image ne doit pas dépasser 5MB' }));
      return;
    }

    setFormData(prev => ({ ...prev, [field]: file }));
    setErrors(prev => ({ ...prev, [field]: null }));

    // Créer une preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviews(prev => ({ ...prev, [field]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = (field) => {
    setFormData(prev => ({ ...prev, [field]: null }));
    setPreviews(prev => ({ ...prev, [field]: null }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validation permis principal (obligatoire)
    if (!formData.primaryDriverLicenseFront) {
      newErrors.primaryDriverLicenseFront = 'Le recto du permis est requis';
    }
    if (!formData.primaryDriverLicenseBack) {
      newErrors.primaryDriverLicenseBack = 'Le verso du permis est requis';
    }
    if (!formData.primaryReferenceNumber || formData.primaryReferenceNumber.trim() === '') {
      newErrors.primaryReferenceNumber = 'Le numéro de référence principal est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Créer FormData pour l'upload
      const formDataToSend = new FormData();
      
      if (formData.primaryDriverLicenseFront) {
        formDataToSend.append('primaryDriverLicenseFront', formData.primaryDriverLicenseFront);
      }
      if (formData.primaryDriverLicenseBack) {
        formDataToSend.append('primaryDriverLicenseBack', formData.primaryDriverLicenseBack);
      }
      if (formData.secondaryDriverLicenseFront) {
        formDataToSend.append('secondaryDriverLicenseFront', formData.secondaryDriverLicenseFront);
      }
      if (formData.secondaryDriverLicenseBack) {
        formDataToSend.append('secondaryDriverLicenseBack', formData.secondaryDriverLicenseBack);
      }
      
      formDataToSend.append('primaryReferenceNumber', formData.primaryReferenceNumber);
      if (formData.secondaryReferenceNumber) {
        formDataToSend.append('secondaryReferenceNumber', formData.secondaryReferenceNumber);
      }

      const response = await kycService.submitKYC(formDataToSend);
      if (response.success) {
        alert('KYC soumis avec succès. En attente de vérification.');
        // Rafraîchir les données utilisateur
        if (refreshUser) {
          await refreshUser();
        }
        onClose();
      }
    } catch (error) {
      console.error('Erreur lors de la soumission du KYC:', error);
      alert(error.message || 'Erreur lors de la soumission. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const ImageUploadCard = ({ field, label, required = false, error }) => {
    const hasImage = previews[field] || formData[field];
    
    return (
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <div className={`w-full h-32 rounded-xl border-2 border-dashed overflow-hidden ${
            error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-gray-50'
          }`}>
            {hasImage ? (
              <div className="relative w-full h-full">
                <img 
                  src={previews[field]} 
                  alt={label}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(field)}
                  className="absolute top-2 right-2 bg-gray-800 bg-opacity-75 hover:bg-opacity-100 text-white p-1.5 rounded-full transition"
                >
                  <X className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => document.getElementById(`${field}-input`).click()}
                  className="absolute top-2 right-12 bg-gray-800 bg-opacity-75 hover:bg-opacity-100 text-white p-1.5 rounded-full transition"
                >
                  <Edit className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <label
                htmlFor={`${field}-input`}
                className="flex flex-col items-center justify-center w-full h-full cursor-pointer hover:bg-gray-100 transition"
              >
                <Upload className="w-8 h-8 text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">
                  {field.includes('Front') ? 'Image recto' : 'Image verso'}
                </span>
              </label>
            )}
          </div>
          <input
            id={`${field}-input`}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleImageUpload(field, e.target.files[0])}
          />
        </div>
        {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      
      {/* Modal */}
      <div className="relative bg-gray-50 rounded-3xl overflow-hidden w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="bg-white px-4 py-4 border-b border-gray-200 flex items-center justify-between">
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h2 className="text-xl font-bold text-gray-900">KYC</h2>
          <div className="w-9"></div> {/* Spacer pour centrer le titre */}
        </div>

        {/* Contenu scrollable */}
        <form onSubmit={handleSubmit} className="overflow-y-auto scrollbar-hide flex-1 pb-20">
          <div className="p-4 space-y-6">
            {/* Permis du premier conducteur */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Permis du premier conducteur <span className="text-red-500">*</span>
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <ImageUploadCard
                  field="primaryDriverLicenseFront"
                  label="Recto"
                  required
                  error={errors.primaryDriverLicenseFront}
                />
                <ImageUploadCard
                  field="primaryDriverLicenseBack"
                  label="Verso"
                  required
                  error={errors.primaryDriverLicenseBack}
                />
              </div>
            </div>

            {/* Numéro de référence principal */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de référence principal <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                value={formData.primaryReferenceNumber}
                onChange={(e) => handleInputChange('primaryReferenceNumber', e.target.value)}
                placeholder="Saisissez votre numéro"
                error={errors.primaryReferenceNumber}
              />
              {errors.primaryReferenceNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.primaryReferenceNumber}</p>
              )}
            </div>

            {/* Permis du deuxième conducteur (optionnel) */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Permis du deuxième conducteur
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <ImageUploadCard
                  field="secondaryDriverLicenseFront"
                  label="Recto"
                  error={errors.secondaryDriverLicenseFront}
                />
                <ImageUploadCard
                  field="secondaryDriverLicenseBack"
                  label="Verso"
                  error={errors.secondaryDriverLicenseBack}
                />
              </div>
            </div>

            {/* Numéro de référence secondaire (optionnel) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de référence secondaire (facultatif)
              </label>
              <PhoneInput
                value={formData.secondaryReferenceNumber}
                onChange={(e) => handleInputChange('secondaryReferenceNumber', e.target.value)}
                placeholder="Saisissez votre numéro"
                error={errors.secondaryReferenceNumber}
              />
            </div>
          </div>
        </form>

        {/* Footer fixe avec bouton */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-4 rounded-b-3xl">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="w-full bg-[#27489E] text-white py-3 rounded-xl font-semibold hover:bg-[#1e3a7d] transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Envoi en cours...</span>
              </>
            ) : (
              <>
                <Check className="w-5 h-5" />
                <span>Appliquer</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default KYCModal;

